import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PlusIcon } from "@heroicons/react/24/outline";
import { AuthContext } from "../../hoc/AuthContext";
import { getApiErrorMessage, jagranNextService, unwrapList } from "../../apiService/jagranNext.service";
import {
  DangerButton,
  EmptyState,
  inputClass,
  Notice,
  PAGE_SIZE,
  PageShell,
  Pager,
  Panel,
  PrimaryButton,
  StatusBadge,
  TextButton,
} from "./ui";

const STATUS_OPTIONS = [
  { value: "", label: "Active and closed" },
  { value: "active", label: "Active" },
  { value: "closed", label: "Closed" },
  { value: "archived", label: "Archived" },
];

export default function CampaignList() {
  const navigate = useNavigate();
  const { selectedDomain } = useContext(AuthContext);
  const productId = selectedDomain?.id;
  const [status, setStatus] = useState("");
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!productId) return undefined;
    let active = true;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const response = await jagranNextService.listCampaigns({
          product_id: productId,
          status,
          limit: PAGE_SIZE,
          offset,
        });
        if (!active) return;
        const list = unwrapList(response.data);
        setRows(list.rows);
        setTotal(list.total);
      } catch (requestError) {
        if (!active) return;
        setRows([]);
        setError(getApiErrorMessage(requestError));
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [productId, status, offset]);

  const runAction = async (campaign, action) => {
    const label = action === "deleteCampaign" ? "delete" : action === "closeCampaign" ? "close" : "archive";
    if (!window.confirm(`Do you want to ${label} ${campaign.name}?`)) return;
    setBusyId(campaign.id);
    setError("");
    try {
      await jagranNextService[action](campaign.id);
      setOffset(0);
      const response = await jagranNextService.listCampaigns({
        product_id: productId,
        status,
        limit: PAGE_SIZE,
        offset: 0,
      });
      const list = unwrapList(response.data);
      setRows(list.rows);
      setTotal(list.total);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <PageShell
      title="Campaigns"
      description="Manage campaigns for the product selected in the header."
      actions={
        <PrimaryButton
          onClick={() => navigate("/products/jagran-next/campaigns/new")}
          disabled={!productId}
        >
          <PlusIcon className="h-4 w-4" />
          New campaign
        </PrimaryButton>
      }
    >
      {!productId ? <Notice>Select a product in the header before loading campaigns.</Notice> : null}
      <Panel>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <label className="block max-w-xs text-sm font-semibold text-gray-700" htmlFor="campaign-status">
            Status
            <select
              id="campaign-status"
              className={`${inputClass} mt-1.5`}
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setOffset(0);
              }}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.label} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
        </div>
        <Notice>{error}</Notice>
        {loading ? <p className="py-8 text-center text-sm text-gray-500">Loading campaigns...</p> : null}
        {!loading && productId && rows.length === 0 ? (
          <EmptyState title="No campaigns yet" description="Create one for the product selected in the header." />
        ) : null}
        {rows.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {rows.map((campaign) => (
              <div key={campaign.id} className="flex flex-wrap items-center justify-between gap-4 py-4">
                <button
                  type="button"
                  className="min-w-0 text-left"
                  onClick={() => navigate(`/products/jagran-next/campaigns/${campaign.id}`)}
                >
                  <span className="flex items-center gap-3">
                    <span className="text-base font-semibold text-gray-900">{campaign.name}</span>
                    <StatusBadge value={campaign.status} />
                  </span>
                  <span className="mt-1 block text-sm text-gray-500">{campaign.slug || "Slug generated after save"}</span>
                </button>
                <div className="flex flex-wrap items-center gap-4">
                  <TextButton onClick={() => navigate(`/products/jagran-next/campaigns/${campaign.id}`)}>Open</TextButton>
                  <TextButton
                    onClick={() => navigate(`/products/jagran-next/campaigns/${campaign.id}/edit`)}
                    disabled={campaign.status === "archived"}
                  >
                    Edit
                  </TextButton>
                  {campaign.status === "active" ? (
                    <TextButton disabled={busyId === campaign.id} onClick={() => runAction(campaign, "closeCampaign")}>Close</TextButton>
                  ) : null}
                  {campaign.status !== "archived" ? (
                    <TextButton disabled={busyId === campaign.id} onClick={() => runAction(campaign, "archiveCampaign")}>Archive</TextButton>
                  ) : null}
                  <DangerButton disabled={busyId === campaign.id} onClick={() => runAction(campaign, "deleteCampaign")}>Delete</DangerButton>
                </div>
              </div>
            ))}
            <Pager offset={offset} limit={PAGE_SIZE} count={rows.length} total={total} onPage={setOffset} />
          </div>
        ) : null}
      </Panel>
    </PageShell>
  );
}
